import { useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import {
  Mouse,
  Type,
  Navigation,
  Camera,
  Scroll,
  Copy,
  Clipboard,
  Search,
  ChevronDown,
  ChevronRight,
  Bot,
  CheckCircle
} from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '../../lib/utils'
import type { StepCategory, StepDefinition } from '../../types/workflow'

// Step definitions organized by category
const stepCategories: StepCategory[] = [
  {
    id: 'ai',
    name: 'AI Actions',
    icon: 'Bot',
    steps: [
      {
        type: 'task',
        name: 'Task',
        description: 'AI-driven automation task',
        icon: 'Bot',
        defaultParams: { description: '' },
        category: 'ai'
      },
      {
        type: 'notify',
        name: 'Notify',
        description: 'Send email notification to your account on workflow completion',
        icon: 'Mail',
        defaultParams: { message: '' },
        category: 'ai'
      }
    ]
  },
  {
    id: 'navigation',
    name: 'Navigation',
    icon: 'Navigation',
    steps: [
      {
        type: 'navigate_url',
        name: 'Navigate to URL',
        description: 'Navigate to a specific URL',
        icon: 'Navigation',
        defaultParams: { url: '', waitForLoad: 5000 },
        category: 'navigation'
      },
      {
        type: 'take_screenshot',
        name: 'Take Screenshot',
        description: 'Capture a screenshot',
        icon: 'Camera',
        defaultParams: { filename: '' },
        category: 'navigation'
      }
    ]
  },
  {
    id: 'validation',
    name: 'Validation',
    icon: 'CheckCircle',
    steps: [
      {
        type: 'verify',
        name: 'Verify',
        description: 'Validate expected conditions',
        icon: 'CheckCircle',
        defaultParams: { description: '' },
        category: 'validation'
      }
    ]
  },
  {
    id: 'mouse',
    name: 'Mouse Actions',
    icon: 'Mouse',
    steps: [
      {
        type: 'mouse_click',
        name: 'Click',
        description: 'Click on an element or coordinates',
        icon: 'Mouse',
        defaultParams: { selector: '', waitFor: 1000 },
        category: 'mouse'
      },
      {
        type: 'mouse_double_click',
        name: 'Double Click',
        description: 'Double-click on an element or coordinates',
        icon: 'Mouse',
        defaultParams: { selector: '' },
        category: 'mouse'
      },
      {
        type: 'drag_drop',
        name: 'Drag & Drop',
        description: 'Drag from source to target',
        icon: 'Mouse',
        defaultParams: { sourceSelector: '', targetSelector: '' },
        category: 'mouse'
      },
      {
        type: 'scroll',
        name: 'Scroll',
        description: 'Scroll at coordinates',
        icon: 'Scroll',
        defaultParams: { x: 640, y: 360, deltaX: 0, deltaY: 100, steps: 10 },
        category: 'mouse'
      }
    ]
  },
  {
    id: 'text',
    name: 'Text & Input',
    icon: 'Type',
    steps: [
      {
        type: 'type_text',
        name: 'Type Text',
        description: 'Type text into an input field',
        icon: 'Type',
        defaultParams: { selector: '', text: '', clearFirst: false },
        category: 'text'
      },
      {
        type: 'keyboard_shortcut',
        name: 'Keyboard Shortcut',
        description: 'Press key combinations',
        icon: 'Type',
        defaultParams: { keys: 'Ctrl+C' },
        category: 'text'
      },
      {
        type: 'copy_selected_text',
        name: 'Copy Selection',
        description: 'Copy currently selected text',
        icon: 'Copy',
        defaultParams: {},
        category: 'text'
      },
      {
        type: 'paste_text',
        name: 'Paste',
        description: 'Paste text at cursor position',
        icon: 'Clipboard',
        defaultParams: {},
        category: 'text'
      },
      {
        type: 'get_clipboard',
        name: 'Get Clipboard',
        description: 'Read clipboard content',
        icon: 'Clipboard',
        defaultParams: { variableName: 'clipboard' },
        category: 'text'
      },
      {
        type: 'set_clipboard',
        name: 'Set Clipboard',
        description: 'Set clipboard content',
        icon: 'Clipboard',
        defaultParams: { text: '' },
        category: 'text'
      }
    ]
  }
]

interface DraggableStepProps {
  step: StepDefinition
  isNarrow?: boolean
}

function DraggableStep({ step, isNarrow = false }: DraggableStepProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: step.type,
    data: {
      type: 'step',
      stepType: step.type
    }
  })

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined

  const IconComponent = getIconComponent(step.icon)

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={cn(
        "flex items-center rounded border bg-card hover:bg-accent cursor-grab transition-colors step-item",
        isNarrow ? "gap-2 p-2 text-xs min-h-[28px]" : "gap-2 p-2 text-xs min-h-[32px]",
        isDragging && "opacity-50"
      )}
      title={`${step.name}: ${step.description}`}
    >
      <div className="flex-shrink-0">
        <IconComponent className={cn(
          "text-muted-foreground",
          isNarrow ? "h-3 w-3" : "h-3 w-3"
        )} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn(
          "font-medium leading-tight",
          isNarrow ? "text-xs" : "text-xs"
        )} style={{
          fontSize: isNarrow ? '11px' : '12px',
          lineHeight: '1.2',
          fontWeight: '500',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {step.name}
        </div>
        {!isNarrow && (
          <div className="text-xs text-muted-foreground leading-tight" style={{
            fontSize: '10px',
            lineHeight: '1.1',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {step.description}
          </div>
        )}
      </div>
    </div>
  )
}

function getIconComponent(iconName: string) {
  switch (iconName) {
    case 'Mouse':
      return Mouse
    case 'Type':
      return Type
    case 'Navigation':
      return Navigation
    case 'Camera':
      return Camera
    case 'Scroll':
      return Scroll
    case 'Copy':
      return Copy
    case 'Clipboard':
      return Clipboard
    case 'Bot':
      return Bot
    case 'CheckCircle':
      return CheckCircle
    default:
      return Mouse
  }
}

interface StepPaletteProps {
  className?: string
}

export function StepPalette({ className }: StepPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['ai', 'navigation', 'validation', 'mouse', 'text'])
  )

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId)
      } else {
        newSet.add(categoryId)
      }
      return newSet
    })
  }

  const filteredCategories = stepCategories.map(category => ({
    ...category,
    steps: category.steps.filter(step =>
      step.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      step.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.steps.length > 0)

  const isNarrow = className?.includes('narrow-step-palette')

  return (
    <div className={cn("h-full flex flex-col bg-background", className)}>
      {!isNarrow && (
        <div className="p-3 border-b bg-muted/30">
          <h3 className="text-sm font-semibold mb-2">Step Palette</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Drag blocks to build your workflow
          </p>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Search steps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-7 h-8 text-xs"
            />
          </div>
        </div>
      )}

      {/* Narrow mode header */}
      {isNarrow && (
        <div className="p-1 border-b bg-muted/30">
          {/* Compact Search */}
          <div className="relative">
            <Search className="absolute left-1 top-1/2 transform -translate-y-1/2 h-2 w-2 text-muted-foreground" />
            <Input
              placeholder="Find..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-5 h-5 text-xs search-input"
              style={{ fontSize: '10px' }}
            />
          </div>
        </div>
      )}

      <div className={cn(
        "flex-1 overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent",
        isNarrow ? "p-1" : "p-2 space-y-2"
      )}>
        {filteredCategories.map(category => {
          const isExpanded = expandedCategories.has(category.id)
          const CategoryIcon = getIconComponent(category.icon)

          return (
            <Collapsible
              key={category.id}
              open={isExpanded}
              onOpenChange={() => toggleCategory(category.id)}
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "w-full justify-start text-xs step-category-button",
                    isNarrow ? "p-2 h-6" : "p-2 h-7"
                  )}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CategoryIcon className={cn(isNarrow ? "h-3 w-3" : "h-3 w-3")} />
                    <span className={cn(
                      "font-medium",
                      isNarrow ? "text-xs" : "text-xs"
                    )} style={{
                      fontSize: isNarrow ? '11px' : '12px',
                      lineHeight: '1.2',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {category.name}
                    </span>
                    {!isNarrow && (
                      <span className="text-xs text-muted-foreground ml-auto" style={{ fontSize: '10px' }}>
                        {category.steps.length}
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronDown className={cn(isNarrow ? "h-3 w-3" : "h-3 w-3")} />
                  ) : (
                    <ChevronRight className={cn(isNarrow ? "h-3 w-3" : "h-3 w-3")} />
                  )}
                </Button>
              </CollapsibleTrigger>

              <CollapsibleContent className={cn(
                "space-y-0.5 mt-0.5",
                isNarrow ? "pl-1" : "pl-2"
              )}>
                {category.steps.map(step => (
                  <DraggableStep key={step.type} step={step} isNarrow={isNarrow} />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )
        })}
        
        {filteredCategories.length === 0 && (
          <div className="text-center py-6 text-muted-foreground">
            <Search className="h-6 w-6 mx-auto mb-2" />
            <p className="text-xs">No steps found</p>
            <p className="text-xs">Try adjusting your search</p>
          </div>
        )}
      </div>
    </div>
  )
}
